/**
 * Removes all child nodes from a specified DOM node.
 *
 * @param {HTMLElement} node - The DOM element from which to remove all child nodes.
 */
export const clear = (node) => {
  while (node.firstChild) {
    node.removeChild(node.firstChild);
  }
};

/**
 * Forces the browser to repaint the specified DOM node.
 *
 * @param {HTMLElement} node - The DOM element to be repainted.
 */
export const repaint = (node) => {
  // eslint-disable-next-line no-void
  void node.offsetHeight;
};

/**
 * Hides the specified DOM nodes by setting their `hidden` attribute to `true`
 * and applying `aria-hidden` for accessibility purposes.
 *
 * @param {...HTMLElement} nodes - One or more DOM elements to hide.
 */
export const hide = (...nodes) => {
  nodes.forEach((node) => {
    // eslint-disable-next-line no-param-reassign
    node.setAttribute('hidden', 'hidden');
    node.setAttribute('aria-hidden', true);
  });
};

/**
 * Shows the specified DOM nodes by setting their `hidden` attribute to `false`
 * and updating the `aria-hidden` attribute for accessibility purposes.
 *
 * @param {...HTMLElement} nodes - One or more DOM elements to show.
 */
export const show = (...nodes) => {
  nodes.forEach((node) => {
    // eslint-disable-next-line no-param-reassign
    node.removeAttribute('hidden');
    node.setAttribute('aria-hidden', false);
  });
};

/**
 * Toggles or forces the `aria-expanded` attribute on a DOM node.
 *
 * @param {HTMLElement} node - The DOM element on which to toggle or set the `aria-expanded` attribute.
 * @param {boolean} [force] - Optional. If true, `aria-expanded` is set to `true`.
 *                            If false, it is set to `false`. If not provided, it toggles the current value.
 */
export const ariaExpandToggle = (node, force) => {
  node.setAttribute(
    'aria-expanded',
    typeof force === 'boolean' ? force : node.getAttribute('aria-expanded') !== 'true',
  );
};

export const hidden = (node) => {
  // eslint-disable-next-line no-param-reassign
  node.classList.add('product__modal--hidden');
  node.classList.remove('product__modal--visible');
};

export const visible = (node) => {
  // eslint-disable-next-line no-param-reassign
  node.classList.remove('product__modal--hidden');
  node.classList.add('product__modal--visible');
};

export const stringToNode = (string, queries = [], alwaysReturnArray = false) => {
  const template = document.createElement('template');
  template.innerHTML = string.trim();

  if (Array.isArray(queries) && queries.length > 0) {
    return queries.map((q) => [...template.content.querySelectorAll(q)]);
  }

  if (template.content.children.length > 1) {
    return [...template.content.children];
  }

  return alwaysReturnArray ? [template.content.firstChild] : template.content.firstChild;
};

export const parseJSONScript = (scriptElement, defaultValue = {}) => {
  try {
    return JSON.parse(scriptElement.innerHTML);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    return defaultValue;
  }
};

export const bundle = (nodes = []) =>
  nodes.reduce((frag, node) => {
    frag.appendChild(node);
    return frag;
  }, new DocumentFragment());

export const useTemplate = (templateNode) => {
  const content = templateNode.content.children[0];

  const populate = (node, data = {}) => {
    const targetRequiredAttribute = node.getAttribute('data-template-show-if');
    const targetAttributes = node.getAttribute('data-template-attributes');
    const templateAttributes = targetAttributes
      ? targetAttributes.split(',').map((s) => s.trim())
      : [];
    const templateContentKey = node.getAttribute('data-template-content');
    const templateHtmlKey = node.getAttribute('data-template-html');

    templateAttributes.forEach((attr) => {
      node.setAttribute(attr, data[attr]);
    });

    if (templateContentKey) {
      node.textContent = data[templateContentKey];
    }

    if (templateHtmlKey) {
      node.innerHTML = data[templateHtmlKey];
    }

    node.removeAttribute('data-template-attributes');
    node.removeAttribute('data-template-content');
    node.removeAttribute('data-template-show-if');

    let hideCurrentNode = false;
    if (
      targetRequiredAttribute &&
      targetRequiredAttribute.length > 0 &&
      (data[targetRequiredAttribute] === undefined ||
        data[targetRequiredAttribute] === null ||
        data[targetRequiredAttribute].length === 0)
    ) {
      hideCurrentNode = true;
      node.hidden = true;
    }

    if (hideCurrentNode === false && node.children) {
      [...node.children].forEach((child) => populate(child, data));
    }

    return node;
  };

  return (data) => {
    const clone = document.importNode(content, true);
    const populatedTemplate = populate(clone, data);

    return populatedTemplate;
  };
};

export default {
  clear,
  repaint,
  stringToNode,
  bundle,
  useTemplate,
};
